import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalType } from '@context/ModalsContext'

import { Modal } from '@ui/components/Modal'
import { Toggle } from '@ui/components/Toggle/Toggle'
import { IconFCType } from '@ui/icons/types'

import { useWindowResize } from '@utils/domUtils'
import { extractValueFromFilterQuery } from '@utils/extractValueFromFilterQuery'
import { createAmountRangeValidator } from '@utils/validations'

import { FilterQueryProps } from '@model/FilterFields'

import { Box } from '@shared/components/Box'

import { FromTo } from '../interfaces/fromTo.interface'

export interface CheckBoxProps {
  label: string
  key: string
  checkedValue?: string
  uncheckedValue?: string
}

interface RangeModalProps {
  name: string
  keys: FromTo
  placeholders?: FromTo
  checkBox?: CheckBoxProps
  InputIcon?: IconFCType
  initialValues?: FilterQueryProps
  onApply?: (values: FilterQueryProps) => void
}

type ErrorBag = Record<string, string | undefined>

export const RangeModal: BaseModalType<RangeModalProps> = (props) => {
  const { t } = useTranslation()
  const {
    name,
    keys,
    close,
    placeholders = { from: t('From'), to: t('To') },
    checkBox,
    InputIcon,
    initialValues,
    onApply,
  } = props
  const isMobile = useWindowResize()
  const [from, setFrom] = useState<string>(
    () => extractValueFromFilterQuery(initialValues?.[keys.from]) ?? ''
  )
  const [to, setTo] = useState<string>(
    () => extractValueFromFilterQuery(initialValues?.[keys.to]) ?? ''
  )
  const [checkVal, setCheckVal] = useState<string | undefined>(() =>
    checkBox ? (initialValues?.[checkBox.key] as string | undefined) : undefined
  )
  const [errors, setErrors] = useState<ErrorBag>({})

  const isChecked = !!checkBox && checkVal === checkBox.checkedValue

  const validate = (): ErrorBag => {
    const values: FilterQueryProps = {
      [keys.from]: from,
      [keys.to]: to,
    }
    const err: ErrorBag = {}

    if (!isChecked) {
      const validator = createAmountRangeValidator(values, err as any, t)
      validator(keys.from, keys.to)
    }

    setErrors(err)
    return err
  }

  const hasErrors = useMemo(() => Object.keys(errors).length > 0, [errors])

  const nothingEntered = !from && !to && !isChecked
  const applyDisabled = (!isChecked && hasErrors) || nothingEntered

  const handleChangeFrom = (v: string | null) => {
    setFrom(v ?? '')
    setErrors((e) => {
      const n = { ...e }
      delete n[keys.from]
      delete n[keys.to]
      return n
    })
  }
  const handleChangeTo = (v: string | null) => {
    setTo(v ?? '')
    setErrors((e) => {
      const n = { ...e }
      delete n[keys.from]
      delete n[keys.to]
      return n
    })
  }

  const toggleCheckbox = (checked: boolean) => {
    if (!checkBox) return

    const next = checked ? checkBox.checkedValue : checkBox.uncheckedValue

    setCheckVal(next)
    setErrors({})
  }

  const handleApply = () => {
    const err = validate()
    if (Object.keys(err).length) return
    const out: FilterQueryProps = {
      [keys.from]: from,
      [keys.to]: to,
    }
    if (checkBox) {
      out[checkBox.key] = checkVal
    }
    onApply?.(out)
    close()
  }

  return (
    <Modal>
      <Modal.Header canClose={!isMobile}>{name}</Modal.Header>
      <Modal.Body>
        <Modal.Body.Table.FromTo<string>
          {...props}
          inputIcon={InputIcon}
          from={{
            hint: errors[keys.from],
            placeholder: placeholders.from,
            value: from,
            error: !!errors[keys.from],
            disabled: isChecked,
            onChange: (e) => handleChangeFrom(e),
          }}
          to={{
            hint: errors[keys.to],
            placeholder: placeholders.to,
            value: to,
            error: !!errors[keys.to],
            disabled: isChecked,
            onChange: (e) => handleChangeTo(e),
          }}
        />
        {checkBox && (
          <Box my='x4' flex align='center' justify='space-between' fullWidth>
            <Modal.Body.Text color='text-high-em'>{checkBox.label}</Modal.Body.Text>

            <Toggle name={checkBox.key} value={isChecked} onChange={toggleCheckbox} />
          </Box>
        )}
      </Modal.Body>
      <Modal.Footer>
        {isMobile && <Modal.Footer.Secondary onClick={close}>{t('Cancel')}</Modal.Footer.Secondary>}
        <Modal.Footer.Primary disabled={applyDisabled} onClick={handleApply}>
          {t('Apply')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}
